import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UseraccessserviceService } from 'src/app/services/useraccessservice.service';
import { OfflineContentReaderService } from 'src/app/services/offline/offline-content-reader.service';
import { OfflineConnectivityService } from 'src/app/services/offline/offline-connectivity.service';
import { getCurrentSchoolSubdomain, getCurrentUserId } from 'src/app/services/offline/offline-cache-key';
import { FullscreenNoteChrome } from 'src/app/services/fullscreen-note-chrome';

interface NotePage {
  page: number;
  title: string;
  html: SafeHtml;
  empty: boolean;
}

// The dedicated read-only lesson/note reader for the downloads hub — reads straight from
// IndexedDB via OfflineContentReaderService, no HttpClient calls at all. Mirrors the online
// lesson page's note pager (class/details/details.component.ts's blocks/currentBlock/
// nextPage/previousPage) for a consistent experience, but notes only — no games, no
// assessments, no admin actions, no cross-lesson next/prev (none of that is fetchable or
// meaningful offline; LessonDetailsModel carries no page-block/game data at all, so the
// exclusion falls out naturally). The only way out of this page is back to the hub (a fixed
// routerLink, not history-back), so "no other navigation" holds regardless of entry point.
@Component({
  selector: 'app-downloadedlesson',
  templateUrl: './downloadedlesson.component.html',
  styleUrls: ['./downloadedlesson.component.css'],
})
export class DownloadedlessonComponent implements OnInit, OnDestroy {
  loading = true;
  notFound = false;
  lessonTitle = '';
  classId = '';
  moduleId: number | null = null;

  pages: NotePage[] = [];
  currentPage = 1;
  get currentBlock(): NotePage | null {
    return this.pages[this.currentPage - 1] ?? null;
  }

  // The live lesson page (class/details/details.component.ts) needs a real connection for
  // almost everything on it — games, assessments, next/prev, admin actions — so only offer
  // the way back to it while there's actually a connection to use. Offline, this link would
  // just get caught by the app-wide block overlay anyway; hiding it here is a clearer signal
  // than showing something that would immediately bounce.
  get canGoToLiveLesson(): boolean {
    return this.connectivity.currentState !== 'offline';
  }

  constructor(
    private route: ActivatedRoute,
    private useraccessservice: UseraccessserviceService,
    private contentReader: OfflineContentReaderService,
    private connectivity: OfflineConnectivityService,
    private sanitizer: DomSanitizer,
  ) {}

  async ngOnInit(): Promise<void> {
    const classId = this.route.snapshot.queryParamMap.get('classid');
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());

    if (!school || !user || !classId) {
      this.notFound = true;
      this.loading = false;
      return;
    }
    this.classId = classId;

    try {
      const lessonContent = await this.contentReader.read<any>(school, user, 'lesson', classId);
      if (!lessonContent) {
        this.notFound = true;
        return;
      }
      this.lessonTitle = lessonContent.title;
      this.moduleId = lessonContent.moduleid;

      const noteContent = await this.contentReader.read<any[]>(school, user, 'note', classId);
      this.pages = (noteContent ?? [])
        .slice()
        .sort((a, b) => (a.page ?? 0) - (b.page ?? 0))
        .map((n, i) => {
          const emptyContent = !n.safeContent || n.safeContent === '<p><br></p>';
          return {
            page: n.page ?? i + 1,
            title: n.title?.length > 0 ? n.title : `Section ${i + 1}`,
            html: this.sanitizer.bypassSecurityTrustHtml(emptyContent ? '' : n.safeContent),
            empty: emptyContent,
          };
        });
    } finally {
      this.loading = false;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.pages.length) this.currentPage++;
  }

  gotoPage(page: number): void {
    this.currentPage = page;
  }

  /* Full-screen reading. Same helper as the live lesson page and the note editor, so the
     three behave identically. */
  readonly fs = new FullscreenNoteChrome();

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.fs.active) {
      this.fs.set(false);
    }
  }

  ngOnDestroy(): void {
    // The scroll lock is on <body>, which outlives this component.
    this.fs.destroy();
  }
}
